import { useEffect, useState } from 'react';
import { useTestContext } from '../../../context/testContext';
import RandomEImg from '../../../assets/img/rightE.bmp';
import { getRandomEScore } from '../../../helpers/functions';
import { useCalibration } from '../../../context/calibrationContext';
import { usePostMessage, useReceiveMessage } from '../../../context/messageContext';
import { getLogmarLineSize500mm } from '../../../helpers/functions';
import { calibrationConstants } from '../../../helpers/calibration-constants';
import { useLogmarData } from '../../../helpers/reducers';
import styled from '@emotion/styled';
import { Button, SessionActions, colors, breakpoints } from '../../../ui';
import { ExamBody, ScoringGrid, Column, Divider, Row, ValueBox, useConfirmNavigation } from '../exams/common';
import AutoResizeControls from '../exams/AutoResizeControls';

// Clinician preview of the tumbling E (Figma session/13 middle). Display only: the patient's
// stimulus size comes from the resizeLogmarImage message below.
const EPreview = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 280px;
  aspect-ratio: 1;
  margin: 0 auto;
  img {
    width: 42%;
    aspect-ratio: 1;
    min-width: 30px;
    image-rendering: pixelated;
    transition: rotate 120ms ease;
  }
  @media (max-width: ${breakpoints.phone - 1}px) {
    max-width: 140px;
  }
  border-radius: 12px;
  background: ${colors.white};
`;

const RandomE = ({ updateStep, eyeTested }) => {
  const {
    calibrationData: { distanceFromScreen, pixelDensity, methodSelected },
  } = useCalibration();
  const { testData, setTestFieldValue, updateTestData } = useTestContext();
  const [currentLogmar, setCurrentLogmar] = useState(testData ? testData[eyeTested] : null);
  const [incorrectSizeCounter, setIncorrectSizeCounter] = useState({ logmarId: 1, counter: 0 });
  const postMessage = usePostMessage();
  const [initialLoad, setInitialLoad] = useState(false);
  const messageFromPatient = useReceiveMessage();
  const { patientDistanceReference } = calibrationConstants;
  const { currentLogmarData, updateLogmarData } = useLogmarData();
  const confirmNavigation = useConfirmNavigation();
  const [rotationDegree, setRotationDegree] = useState(0);

  useEffect(() => {
    updateTestData({ testInProgress: true });
  }, [updateTestData]);

  const selectNextLogmar = () => {
    if (currentLogmar.id === 13) return finishTest();
    return updateLogmarData({ type: 'decrementLogmarSize' });
  };

  const selectPreviousLogmar = () => {
    if (currentLogmar.id > 1 && currentLogmar.id > testData.maxPermittedLogmar.id) return updateLogmarData({ type: 'incrementLogmarSize' });
  };

  const markCorrectAnswer = () => {
    selectNextLogmar();
  };

  const markIncorrectAnswer = () => {
    if (currentLogmar.id === incorrectSizeCounter['logmarId']) {
      if (incorrectSizeCounter['counter'] > 0) {
        finishTest();
      }
    }

    setIncorrectSizeCounter({ logmarId: currentLogmar.id, counter: 1 });
    selectPreviousLogmar();
  };

  const markRepeatAnswer = () => {
    const rotation = changeE();
    updateLogmarContentSize(currentLogmar, rotation, distanceFromScreen);
  };

  const changeE = () => {
    const randomRotationDegree = Math.floor(Math.random() * 4) * 90;
    if (randomRotationDegree === rotationDegree) return changeE();
    setRotationDegree(randomRotationDegree);
    return randomRotationDegree;
  };

  const finishTest = () => {
    let logmarForScore = currentLogmar.id - 1;
    if (currentLogmar.id === 13) {
      logmarForScore = 13;
    }
    const score = { score: getRandomEScore(logmarForScore) };
    setTestFieldValue({
      name: eyeTested,
      value: score,
    });
    const nextStep = eyeTested === 'rightEye' && testData.testLeftEye ? 'leftEyeSetup' : 'results';
    return updateStep(nextStep);
  };

  const getLogmarContentSizeRefactor = (logmar, distance) => {
    let lineSize;
    if (patientDistanceReference === 500) {
      lineSize = getLogmarLineSize500mm(logmar.id);
    }
    const contentSize = (lineSize * distance) / patientDistanceReference / pixelDensity;
    return contentSize;
  };

  const updateLogmarContentSize = (logmar, rotation, distance) => {
    const contentSize = Math.round(getLogmarContentSizeRefactor(logmar, distance));
    postMessage({
      type: 'resizeLogmarImage',
      content: {
        contentSize: contentSize,
        distanceReferenceInMM: distance,
        rotationDegree: rotation,
      },
    });
  };

  useEffect(() => {
    if (initialLoad === false) {
      updateLogmarData({ type: 'setInitialLogmarSize', payload: testData[eyeTested] });

      postMessage({
        type: 'randomE',
        content: {
          methodSelected: methodSelected,
          testMode: methodSelected !== 'manual' ? 'auto' : testData.testMode,
          distanceFromScreen: distanceFromScreen,
          rotationDegree: rotationDegree,
        },
      });

      setInitialLoad(true);
    }
  }, [initialLoad]);

  useEffect(() => {
    const logmar = currentLogmarData.logmarSize;
    const rotation = changeE();
    setCurrentLogmar(logmar);
    updateLogmarContentSize(logmar, rotation, distanceFromScreen);
  }, [currentLogmarData.logmarSize, distanceFromScreen]);

  useEffect(() => {
    if (messageFromPatient.type === 'patientMovementDetected') {
      if (messageFromPatient.content.patientIsMoving === false) {
        const rotation = changeE();
        updateLogmarContentSize(currentLogmar, rotation, messageFromPatient.content.patientDistance);
      }
    }
  }, [messageFromPatient]);

  return (
    <ExamBody>
      <ScoringGrid left='1fr' right='1.1fr' align='center' fill>
        <EPreview aria-label={`Tumbling E preview, rotated ${rotationDegree} degrees`}>
          <img src={RandomEImg} alt='' style={{ rotate: `${rotationDegree}deg` }} />
        </EPreview>
        <Column gap={16}>
          <ValueBox large label='Current logMAR Size' value={currentLogmar?.value ?? '—'} />
          <Row gap={12}>
            <Button variant='primary' size='lg' onClick={() => markCorrectAnswer()}>
              Correct
            </Button>
            <Button variant='destructive' size='lg' onClick={() => markIncorrectAnswer()}>
              Incorrect
            </Button>
            <Button variant='secondary' size='lg' onClick={() => markRepeatAnswer()}>
              Repeat
            </Button>
          </Row>
          <Divider />
          <AutoResizeControls />
        </Column>
      </ScoringGrid>
      <SessionActions
        onBack={() =>
          confirmNavigation().then(() => {
            updateTestData({ [eyeTested]: null, testInProgress: false });
            updateStep(`${eyeTested}Setup`);
          })
        }
      />
    </ExamBody>
  );
};

export default RandomE;
